import { HttpClient } from './HttpClient.js';
import { fromTeamDataList, toTeamOnboardRequest, toTeamUpdate } from './dto/TeamData.js';

/**
 * TeamService - Handles team management operations
 * Uses HttpClient directly since these endpoints are on the identity server
 */
export class TeamService {
  constructor() {
    this.httpClient = new HttpClient();
    
    // Environment configuration
    this.authBaseUrl = process.env.REACT_APP_IDENTITY_SERVER_URL;
    
    console.log('TeamService initialized');
  }

  /**
   * Fetch all teams for the signed-in user's clinic
   * @returns {Promise<import('./dto/TeamData.js').TeamData[]>}
   */
  async fetchTeams(options = {}) {
    const response = await this.httpClient.get(`${this.authBaseUrl}/api/teams/fetch`, options);
    this.httpClient.checkResponse(response);
    return fromTeamDataList(await this.httpClient.parseJson(response));
  }

  /**
   * Create a new team
   * @param {string} teamName - The name of the team to create
   * @returns {Promise<void>}
   */
  async createTeam(teamName, options = {}) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/team/create`,
      toTeamOnboardRequest({ teamName }),
      options
    );
    this.httpClient.checkResponse(response);
  }

  /**
   * Update a team (name and/or members)
   * @param {Object} teamUpdate - The team update data
   * @param {string} teamUpdate.teamId - The ID of the team to update
   * @param {string} [teamUpdate.teamName] - Optional new team name
   * @param {Array<string>} [teamUpdate.addedUserIds] - Optional list of user IDs to add
   * @param {Array<string>} [teamUpdate.removedUserIds] - Optional list of user IDs to remove
   * @returns {Promise<void>}
   */
  async updateTeam(teamUpdate, options = {}) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/team/update`,
      toTeamUpdate(teamUpdate),
      options
    );
    this.httpClient.checkResponse(response);
  }

  /**
   * Delete a team
   * @param {string} teamId - The ID of the team to delete
   * @returns {Promise<void>}
   */
  async deleteTeam(teamId, options = {}) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/team/delete`,
      toTeamUpdate({ teamId }),
      options
    );
    this.httpClient.checkResponse(response);
  }
}

